import { Component, Input, OnDestroy, OnInit } from "@angular/core";
import { ModalController } from "@ionic/angular";
import { interval, Subscription } from "rxjs";
import { App } from "@capacitor/app";
import {
  GuidePermissionKey,
  GuidePermissionState,
  PermissionManagerService,
} from "src/app/_services/permission-manager.service";

interface PermissionStepItem {
  key: GuidePermissionKey;
  titleKey: string;
  descriptionKey: string;
  actionKey: string;
}

@Component({
  selector: "app-permission-stepper",
  templateUrl: "./permission-stepper.component.html",
  styleUrls: ["./permission-stepper.component.scss"],
})
export class PermissionStepperComponent implements OnInit, OnDestroy {
  @Input() filterKeys?: GuidePermissionKey[];

  steps: PermissionStepItem[] = [];
  state: GuidePermissionState | null = null;
  currentIndex = 0;
  loading = false;
  private pollSub?: Subscription;
  private appResumeListener?: { remove: () => Promise<void> } | undefined;
  private readonly STORAGE_KEY = "permission_stepper_state_v1";
  private seenSteps = new Set<number>();
  completed = false;

  constructor(
    private permissionManager: PermissionManagerService,
    private modalController: ModalController
  ) {}

  async ngOnInit(): Promise<void> {
    const defaultSteps: PermissionStepItem[] = [
      {
        key: "location",
        titleKey: "PERMISSIONS.STEPPER.LOCATION_TITLE",
        descriptionKey: "PERMISSIONS.STEPPER.LOCATION_DESCRIPTION",
        actionKey: "PERMISSIONS.STEPPER.ACTION_LOCATION",
      },
      {
        key: "notifications",
        titleKey: "PERMISSIONS.STEPPER.NOTIFICATIONS_TITLE",
        descriptionKey: "PERMISSIONS.STEPPER.NOTIFICATIONS_DESCRIPTION",
        actionKey: "PERMISSIONS.STEPPER.ACTION_NOTIFICATIONS",
      },
      {
        key: "battery",
        titleKey: "PERMISSIONS.STEPPER.BATTERY_TITLE",
        descriptionKey: "PERMISSIONS.STEPPER.BATTERY_DESCRIPTION",
        actionKey: "PERMISSIONS.STEPPER.ACTION_BATTERY",
      },
    ];

    const filterKeys = this.filterKeys;
    this.steps = filterKeys?.length
      ? defaultSteps.filter((step) => filterKeys.includes(step.key))
      : defaultSteps;

    this.loadState();
    if (!this.filterKeys?.length) {
      this.seenSteps.add(this.currentIndex);
      this.saveState();
    }

    await this.refreshState();
    if (!this.alignCurrentStepWithState()) {
      this.close();
      return;
    }
    this.autoAdvance();
    this.pollSub = interval(30000).subscribe(() => this.refreshState());
    this.appResumeListener = await App.addListener("appStateChange", ({ isActive }) => {
      if (!isActive) return;
      setTimeout(async () => {
        await this.refreshState();
        if (!this.alignCurrentStepWithState()) {
          this.close();
          return;
        }
        this.autoAdvance();
      }, 500);
    });
  }

  ngOnDestroy(): void {
    this.pollSub?.unsubscribe();
    this.appResumeListener?.remove();
  }

  get currentStep(): PermissionStepItem | null {
    return this.steps[this.currentIndex] ?? null;
  }

  get canShowStep(): boolean {
    return !!this.currentStep;
  }

  async requestCurrentPermission(): Promise<void> {
    if (!this.currentStep) return;
    this.loading = true;
    try {
      await this.permissionManager.requestPermission(this.currentStep.key);
      await this.refreshState();
      this.autoAdvance();
    } finally {
      this.loading = false;
    }
  }

  skipCurrentStep(): void {
    if (this.isFilteredMode) {
      this.close();
      return;
    }
    this.seenSteps.add(this.currentIndex);
    this.currentIndex += 1;
    this.saveState();
    if (this.currentIndex >= this.steps.length) {
      this.completed = true;
      this.saveState();
      this.close();
    }
  }

  close(): void {
    this.modalController.dismiss();
  }

  private async refreshState(): Promise<void> {
    try {
      this.state = await this.permissionManager.checkPermissionState();
    } catch (error) {
      console.warn("[PermissionStepper] refreshState failed, using safe fallback", error);
      this.state = {
        locationStatus: "denied",
        notificationsGranted: false,
        batteryOptimizationIgnored: false,
        autostartEnabled: false,
        displayPopupsEnabled: false,
      };
    }
  }

  private autoAdvance(): void {
    if (!this.state) return;
    let progressed = false;
    if (
      this.isFilteredMode &&
      this.currentStep &&
      this.permissionManager.isGranted(this.currentStep.key, this.state)
    ) {
      this.close();
      return;
    }
    while (this.currentIndex < this.steps.length) {
      const step = this.steps[this.currentIndex];
      const granted = this.permissionManager.isGranted(step.key, this.state);
      if (!granted) return;
      this.seenSteps.add(this.currentIndex);
      this.currentIndex += 1;
      progressed = true;
    }

    if (progressed) {
      this.saveState();
    }

    if (this.currentIndex >= this.steps.length) {
      this.completed = true;
      this.saveState();
      this.close();
    }
  }

  private alignCurrentStepWithState(): boolean {
    if (!this.state) return !!this.currentStep;
    const firstMissingIndex = this.steps.findIndex(
      (step) => !this.permissionManager.isGranted(step.key, this.state!)
    );
    const nextIndex = firstMissingIndex === -1 ? this.steps.length : firstMissingIndex;
    const hasChanged = this.currentIndex !== nextIndex || this.completed !== (firstMissingIndex === -1);

    this.currentIndex = nextIndex;
    this.completed = firstMissingIndex === -1;

    if (hasChanged) {
      this.saveState();
    }

    return firstMissingIndex !== -1;
  }

  permGranted(key: GuidePermissionKey): boolean {
    if (!this.state) return false;
    return this.permissionManager.isGranted(key, this.state);
  }

  isAutoVerifiable(_key: GuidePermissionKey): boolean {
    return true;
  }

  get isFilteredMode(): boolean {
    return !!this.filterKeys?.length;
  }

  get locationStepDescription(): string {
    return "PERMISSIONS.STEPPER.LOCATION_DESCRIPTION";
  }

  private loadState(): void {
    if (this.filterKeys?.length) return;
    try {
      const raw = localStorage.getItem(this.STORAGE_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw) as {
        currentIndex?: number;
        seenSteps?: number[];
        completed?: boolean;
      };
      if (typeof parsed.currentIndex === "number") {
        this.currentIndex = Math.min(
          Math.max(parsed.currentIndex, 0),
          Math.max(this.steps.length - 1, 0)
        );
      }
      if (Array.isArray(parsed.seenSteps)) {
        this.seenSteps = new Set(parsed.seenSteps.filter((i) => i >= 0 && i < this.steps.length));
      }
      this.completed = !!parsed.completed;
    } catch {
      this.currentIndex = 0;
      this.seenSteps.clear();
      this.completed = false;
    }
  }

  private saveState(): void {
    if (this.filterKeys?.length) return;
    try {
      localStorage.setItem(
        this.STORAGE_KEY,
        JSON.stringify({
          currentIndex: this.currentIndex,
          seenSteps: Array.from(this.seenSteps.values()),
          completed: this.completed,
        })
      );
    } catch {
      // ignore
    }
  }
}
